import { Injectable, NgZone, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class UserpilotBeaconStyleService implements OnDestroy {
  private static readonly FRAME_ID = 'userpilot-resource-centre-frame';
  private static readonly BEACON_ID = 'userpilot-resource-centre-beacon';
  private static readonly STYLE_ID = 'app-userpilot-beacon-style';

  private observer?: MutationObserver;
  private pollTimer?: ReturnType<typeof setInterval>;
  private crossOriginWarned = false;
  private cssFactory: () => string = () => '';

  constructor(private readonly zone: NgZone) {}

  resolveHostCssVar(name: string): string {
    const varName = name.startsWith('--') ? name : `--${name}`;
    return getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
  }

  apply(css: string | (() => string)): void {
    this.cssFactory = typeof css === 'function' ? css : () => css;

    // Run outside Angular so the polling/observer don't trigger change detection.
    this.zone.runOutsideAngular(() => {
      // Try immediately in case the frame already exists.
      this.injectIntoFrame();

      // Userpilot injects and re-renders the frame asynchronously (and on every
      // Userpilot.reload() call). Watch the DOM so we re-apply when it reappears.
      this.observer = new MutationObserver(() => this.injectIntoFrame());
      this.observer.observe(document.documentElement, {
        childList: true,
        subtree: true
      });

      this.pollTimer = setInterval(() => this.injectIntoFrame(), 1500);
    });
  }

  /** Convenience helper to set just the beacon background color. */
  applyBackgroundColor(color: string): void {
    this.apply(
      `#${UserpilotBeaconStyleService.BEACON_ID} { background: ${color} !important; }`
    );
  }

  private injectIntoFrame(): void {
    const frame = document.getElementById(
      UserpilotBeaconStyleService.FRAME_ID
    ) as HTMLIFrameElement | null;

    if (!frame) return;

    let doc: Document | null = null;
    try {
      // Accessing contentDocument on a cross-origin frame returns null or throws.
      doc = frame.contentDocument ?? frame.contentWindow?.document ?? null;
    } catch {
      this.warnCrossOriginOnce();
      return;
    }

    if (!doc || !doc.head) {
      if (frame.contentWindow && !frame.contentDocument) {
        this.warnCrossOriginOnce();
        this.stopPolling();
      }
      return;
    }

    let style = doc.getElementById(
      UserpilotBeaconStyleService.STYLE_ID
    ) as HTMLStyleElement | null;

    if (!style) {
      style = doc.createElement('style');
      style.id = UserpilotBeaconStyleService.STYLE_ID;
      doc.head.appendChild(style);
    }

    // Re-evaluate the factory each pass so theme-derived values stay current.
    const css = this.cssFactory();
    if (style.textContent !== css) {
      style.textContent = css;
    }
  }

  private warnCrossOriginOnce(): void {
    if (this.crossOriginWarned) return;
    this.crossOriginWarned = true;
    // eslint-disable-next-line no-console
    console.warn(
      '[UserpilotBeaconStyleService] The Userpilot resource centre iframe is ' +
        'cross-origin, so its beacon cannot be styled from the app. Set the ' +
        'beacon color in the Userpilot dashboard "Style" tab instead.'
    );
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.stopPolling();
  }
}
